import { ROOM_CODE_ALPHABET, ROOM_CODE_LENGTH } from './config';

/** Browser navigation paths and API paths shared by the Worker router, projections and the client. */

export function roomPath(code: string): string {
  return `/r/${code}`;
}

export function resultPath(roundId: string): string {
  return `/results/${encodeURIComponent(roundId)}`;
}

const ROOM_PATH_RE = new RegExp(`^/r/([${ROOM_CODE_ALPHABET}]{${ROOM_CODE_LENGTH}})$`, 'i');
const RESULT_PATH_RE = /^\/results\/([^/]+)$/;

export function parseRoomPath(pathname: string): string | null {
  return ROOM_PATH_RE.exec(pathname)?.[1]?.toUpperCase() ?? null;
}

export function parseResultPath(pathname: string): string | null {
  const match = RESULT_PATH_RE.exec(pathname)?.[1];
  if (!match) return null;
  try {
    return decodeURIComponent(match);
  } catch {
    return null;
  }
}

/** Prefixes that select the API and WebSocket surfaces; concrete paths are below. */
const API_PREFIX = '/api/';
const SOCKET_PREFIX = '/ws/';

export function isApiPath(pathname: string): boolean {
  return pathname.startsWith(API_PREFIX);
}

export function isSocketPath(pathname: string): boolean {
  return pathname.startsWith(SOCKET_PREFIX);
}

export const API_PATHS = {
  health: '/api/health',
  diagnostics: '/api/diagnostics',
  feedback: '/api/feedback',
  profile: '/api/profile',
  daily: '/api/daily',
  rooms: '/api/rooms',
  /** Rooms the authenticated profile belongs to; matched before the `/api/rooms/:code` lookup. */
  myRooms: '/api/rooms/mine',
  history: '/api/history',
  results: '/api/results',
  leaderboard: '/api/leaderboard',
  scoreboard: '/api/scoreboard',
  /** The credential's visible Competitive rank. */
  ranked: '/api/ranked',
  /** Placed Competitive players by tier and points. */
  ladder: '/api/ladder',
} as const;

/** The Competitive matchmaking queue; room codes are six characters, so it never collides. */
export const QUEUE_SOCKET_PATH = `${SOCKET_PREFIX}queue`;

export function roomApiPath(code: string): string {
  return `${API_PATHS.rooms}/${encodeURIComponent(code)}`;
}

export function roomCancelApiPath(code: string): string {
  return `${roomApiPath(code)}/cancel`;
}

export function resultApiPath(roundId: string): string {
  return `${API_PATHS.results}/${encodeURIComponent(roundId)}`;
}

export function roomSocketPath(code: string): string {
  return `${SOCKET_PREFIX}${encodeURIComponent(code)}`;
}

/** The raw segment between `prefix` and `suffix`, or null for any other path shape. */
function segmentBetween(pathname: string, prefix: string, suffix = ''): string | null {
  if (!pathname.startsWith(prefix) || !pathname.endsWith(suffix)) return null;
  const segment = pathname.slice(prefix.length, pathname.length - suffix.length);
  return segment && !segment.includes('/') ? segment : null;
}

/** Parsers return the raw segment; callers validate it as a room code or round ID. */
export function parseRoomApiPath(pathname: string): string | null {
  return segmentBetween(pathname, `${API_PATHS.rooms}/`);
}

export function parseRoomCancelApiPath(pathname: string): string | null {
  return segmentBetween(pathname, `${API_PATHS.rooms}/`, '/cancel');
}

export function parseResultApiPath(pathname: string): string | null {
  return segmentBetween(pathname, `${API_PATHS.results}/`);
}

export function parseRoomSocketPath(pathname: string): string | null {
  return segmentBetween(pathname, SOCKET_PREFIX);
}
